import { pick } from 'lodash';
import { autocalculation } from "../autocalculation";
import { isProtocolLink, } from "../entities";
import { FIXED_PROTOCOL_CONTENT_TYPE_IDS } from './ProtocolTypeUtils';
import { findPredecessorEntity, getLatestObjectsWithHistory } from './VersioningUtils';
export function getEquipmentItemSchema(equipmentItem, equipmentList, protocolContentType) {
    const adjustments = equipmentList.protocolContentTypes.find((config) => config.ref._id === protocolContentType._id)?.adjustments || [];
    return protocolContentType.schema.reduce((schema, element) => {
        if (element.type === undefined) {
            return [...schema, element];
        }
        const config = equipmentItem.contentTypeConfig.find((config) => config.contentType._id === protocolContentType._id && config.elementId === element._id);
        if (config && config.disabled) {
            return [...schema];
        }
        const adjustment = adjustments.find((adjustment) => adjustment.elementId === element._id);
        if (!adjustment) {
            return [...schema, element];
        }
        const itemElementForCount = equipmentList.itemSchema.find((itemElement) => itemElement.type !== undefined && itemElement._id === adjustment.itemElementIdForCount);
        const count = itemElementForCount ? equipmentItem.itemData[itemElementForCount.name] : 1;
        if (count === 1) {
            return [...schema, element];
        }
        const repeatedElement = Array.from({ length: count }, (_, i) => i + 1).map((i) => ({
            ...element,
            name: `${element.name}_${i}`,
            label: `${element.label} (${i})`,
        }));
        return [...schema, ...repeatedElement];
    }, []);
}
export function getChecklistSchema(checklistType) {
    return checklistType.checkpoints.map((checkpoint) => {
        if (checkpoint.type === 'checkbox') {
            return { _id: checkpoint._id, type: checkpoint.type, name: checkpoint._id, label: checkpoint.text };
        }
        return {
            _id: checkpoint._id,
            type: checkpoint.type,
            name: checkpoint._id,
            label: checkpoint.text,
            items: checkpoint.options.map((option) => ({ value: option.option, label: option.option })),
        };
    });
}
export function getRemarksSchema(label) {
    return [
        {
            _id: FIXED_PROTOCOL_CONTENT_TYPE_IDS.REMARKS,
            name: 'remarks',
            label: label,
            type: 'textarea',
            validation: [],
        },
    ];
}
export function isElementForConditional(element) {
    if (element.type === undefined) {
        return false;
    }
    return (element.type === 'autocomplete' ||
        element.type === 'combobox' ||
        element.type === 'checkbox' ||
        element.type === 'number');
}
export function repeatSchema(schema, number) {
    if (number <= 0) {
        throw Error(`Invalid repetition number ${number}`);
    }
    return schema.map((element) => {
        if (element.type === undefined) {
            return element;
        }
        return { ...element, name: `${element.name}_${number}` };
    });
}
export function getRepeatedSchema(schema, count) {
    if (count === 1) {
        return [schema];
    }
    return Array.from({ length: count }, (_, i) => i + 1).map((i) => repeatSchema(schema, i));
}
export function getElementValue(protocol, contentType, elementId) {
    const content = protocol.contents.find((content) => content.type._id === contentType._id);
    const element = getElement(contentType, elementId);
    if (!element || !content) {
        return undefined;
    }
    const elementValue = content.data[element.name];
    return elementValue;
}
export function getGroupItems(protocol, contentType, groupElement) {
    const groupItems = getElementValue(protocol, contentType, groupElement._id);
    if (!Array.isArray(groupItems)) {
        return [];
    }
    return groupItems;
}
export function getSubElementValue(data, groupElement, subElementId) {
    const subElement = getElement(getGroupChildrenElements(groupElement), subElementId);
    if (!subElement) {
        return undefined;
    }
    return data[subElement.name];
}
export function getElements(contentType) {
    const schema = Array.isArray(contentType) ? contentType : contentType.schema;
    return schema.filter((element) => element.type !== undefined);
}
export function getGroupChildrenElements(group) {
    if (group === undefined || group.type !== 'group' || !group.children || !Array.isArray(group.children)) {
        return [];
    }
    return getElements(group.children);
}
export function getElement(contentType, elementId) {
    return getElements(contentType).find((element) => element._id === elementId);
}
export function getSchemaElementNames(schema) {
    return getElements(schema || []).map((element) => element.name);
}
export function cleanupSchemaData(schema, oldSchema, data) {
    const elements = getElements(schema);
    const oldElements = getElements(oldSchema);
    const elementsMap = elements.reduce((map, element) => ({ ...map, [element._id]: element }), {});
    const oldElementsMap = oldElements.reduce((map, oldElement) => ({ ...map, [oldElement._id]: oldElement }), {});
    const cleanedData = pick(data, elements.map((element) => element.name));
    Object.entries(oldElementsMap).forEach(([id, oldElement]) => {
        const element = elementsMap[id];
        if (element && oldElement && element.name !== oldElement.name) {
            cleanedData[element.name] = data[oldElement.name];
        }
    });
    elements
        .filter((element) => element.type === 'group')
        .forEach((element) => {
        const children = element.children;
        if (Array.isArray(children) && cleanedData[element.name]) {
            const items = cleanedData[element.name];
            const oldElement = oldElementsMap[element._id];
            const oldChildren = oldElement && oldElement.type === 'group' && Array.isArray(oldElement.children) ? oldElement.children : [];
            cleanedData[element.name] = items.map((item) => cleanupSchemaData(children, oldChildren, item));
        }
    });
    elements
        .filter((element) => element.type === 'object')
        .forEach((element) => {
        const children = element.children;
        if (Array.isArray(children) && cleanedData[element.name]) {
            const oldElement = oldElementsMap[element._id];
            const oldChildren = oldElement && oldElement.type === 'group' && Array.isArray(oldElement.children) ? oldElement.children : [];
            cleanedData[element.name] = cleanupSchemaData(children, oldChildren, cleanedData[element.name]);
        }
    });
    return cleanedData;
}
export function getHistoricProtocolContentType(app, protocol, protocolContentType) {
    if (!('_id' in protocol.type)) {
        return undefined;
    }
    const type = app.get('localCache').getProtocolType(protocol.type._id);
    return findPredecessorEntity(app.get('localCache').getProtocolContentTypes(true), app.get('localCache').getProtocolContentType(protocolContentType._id), type.contentTypes.map((ct) => ct.ref._id));
}
export function transformHistoricProtocolContentData(app, protocol, protocolTypeRef) {
    const localCache = app.get('localCache');
    const protocolType = localCache.getProtocolType(protocolTypeRef._id);
    const elementMappings = protocolType.contentTypes
        .map(({ ref }) => {
        const contentType = localCache.getProtocolContentType(ref._id);
        const historicContentType = getHistoricProtocolContentType(app, protocol, contentType);
        if (!historicContentType) {
            return {};
        }
        return getElements(contentType).reduce((allElements, element) => {
            const sourceElement = getElement(historicContentType, element._id);
            if (sourceElement) {
                allElements[element.name] = sourceElement.name;
            }
            return allElements;
        }, {});
    })
        .reduce((allMappings, currentMapping) => ({ ...allMappings, ...currentMapping }), {});
    const historicData = protocol.contents.reduce((allContent, content) => ({ ...allContent, ...content.data }), {});
    const data = Object.entries(elementMappings).reduce((mappedData, [targetElementName, sourceElementName]) => {
        mappedData[targetElementName] = historicData[sourceElementName];
        return mappedData;
    }, {});
    return data;
}
export function matchesElementValue(app, protocol, condition) {
    if (!condition) {
        return true;
    }
    const protocolContentType = getHistoricProtocolContentType(app, protocol, condition.protocolContentType);
    if (!protocolContentType) {
        return false;
    }
    let conditionValue = getElementValue(protocol, protocolContentType, condition.elementId);
    if (isProtocolLink(conditionValue)) {
        return conditionValue.value === condition.value;
    }
    const element = getElement(protocolContentType, condition.elementId);
    if (element?.type === 'checkbox') {
        if (conditionValue === undefined) {
            conditionValue = false;
        }
        if (condition.value === undefined) {
            condition = { ...condition, value: false };
        }
    }
    if (element?.type === 'autocomplete' &&
        element?.multiple &&
        typeof conditionValue === 'string' &&
        typeof condition.value === 'string') {
        const conditionElementValues = conditionValue.split(',');
        return conditionElementValues.includes(condition.value);
    }
    if (element?.type === 'equipmentitem' && typeof conditionValue === 'string' && typeof condition.value === 'string') {
        const expectedEquipmentItemId = condition.value;
        const allEquipmentItems = getLatestObjectsWithHistory(app.get('localCache').getEquipmentItems(true)).map(({ data, history }) => [...history, data]);
        const equipmentItemHistory = allEquipmentItems.find((history) => history.some((item) => item._id === expectedEquipmentItemId));
        const equipmentItemIds = (equipmentItemHistory || []).map((item) => item._id);
        return equipmentItemIds.includes(conditionValue);
    }
    return conditionValue === condition.value;
}
export function withPeerDependencies(contentType, getProtocolContentType, existingDependencies = []) {
    if (typeof getProtocolContentType !== 'function') {
        return withPeerDependencies(contentType, (id) => getProtocolContentType.get('localCache').getProtocolContentType(id), existingDependencies);
    }
    const contentTypesToHandle = [contentType];
    const peerDependencies = [];
    const allExistingDependencies = existingDependencies.map((dependency) => dependency._id);
    while (contentTypesToHandle.length > 0) {
        const currentContentType = contentTypesToHandle.shift();
        if (currentContentType) {
            if (!allExistingDependencies.includes(currentContentType._id)) {
                peerDependencies.push(currentContentType);
                allExistingDependencies.push(currentContentType._id);
            }
            contentTypesToHandle.push(...(currentContentType.peerDependencies || [])
                .filter((ref) => !allExistingDependencies.includes(ref._id))
                .map((ref) => getProtocolContentType(ref._id))
                .filter((contentType) => contentType !== undefined));
        }
    }
    return peerDependencies;
}
export function areElementNamesUnique(contentTypes) {
    const overallSchema = contentTypes.flatMap(({ schema }) => schema);
    const elementNames = getSchemaElementNames(overallSchema);
    return elementNames.length === new Set(elementNames).size;
}
const defaultFilterDataBySchemaOptions = {
    peerDependencySchema: [],
    peerDependencyValue: {},
    parentSchema: [],
    parentValue: {},
};
export function areElementConditionalsMatching(element, schema, data, options) {
    if (!element.conditional) {
        return true;
    }
    const { peerDependencySchema, peerDependencyValue, parentSchema, parentValue } = options;
    const conditions = !Array.isArray(element.conditional) ? [element.conditional] : element.conditional;
    return conditions.every(({ elementId: conditionElementId, value: conditionalValue, negateCondition }) => {
        const conditionElement = [...schema, ...peerDependencySchema].find((otherElement) => otherElement.type !== undefined && otherElement._id === conditionElementId);
        if (conditionElement) {
            const allData = { ...data, ...peerDependencyValue };
            let actualConditionElementValue = allData[conditionElement.name];
            if (conditionElement.type === 'checkbox' && actualConditionElementValue === undefined) {
                actualConditionElementValue = false;
            }
            if (conditionElement.type === 'number' && typeof conditionalValue === 'string') {
                if (typeof actualConditionElementValue !== 'number') {
                    return false;
                }
                const formula = `${actualConditionElementValue} ${conditionalValue}`;
                const result = autocalculation.execute(formula, allData);
                return negateCondition ? !result : result;
            }
            if (conditionElement.type === 'autocomplete' &&
                conditionElement.multiple &&
                typeof actualConditionElementValue === 'string' &&
                typeof conditionalValue === 'string') {
                const conditionElementValues = actualConditionElementValue.split(',');
                if (negateCondition) {
                    return !conditionElementValues.includes(conditionalValue);
                }
                return conditionElementValues.includes(conditionalValue);
            }
            if (negateCondition) {
                return actualConditionElementValue !== conditionalValue;
            }
            return actualConditionElementValue === conditionalValue;
        }
        const conditionElementFromParent = parentSchema.find((otherElement) => otherElement.type !== undefined && otherElement._id === conditionElementId);
        if (conditionElementFromParent) {
            let actualConditionElementFromParentValue = parentValue[conditionElementFromParent.name];
            if (conditionElementFromParent.type === 'checkbox' && actualConditionElementFromParentValue === undefined) {
                actualConditionElementFromParentValue = false;
            }
            if (conditionElementFromParent.type === 'number' && typeof conditionalValue === 'string') {
                if (typeof actualConditionElementFromParentValue !== 'number') {
                    return false;
                }
                const formula = `${actualConditionElementFromParentValue} ${conditionalValue}`;
                const result = autocalculation.execute(formula, parentValue);
                return negateCondition ? !result : result;
            }
            if (conditionElementFromParent.type === 'autocomplete' &&
                conditionElementFromParent.multiple &&
                typeof actualConditionElementFromParentValue === 'string' &&
                typeof conditionalValue === 'string') {
                const conditionElementValues = actualConditionElementFromParentValue.split(',');
                if (negateCondition) {
                    return !conditionElementValues.includes(conditionalValue);
                }
                return conditionElementValues.includes(conditionalValue);
            }
            if (negateCondition) {
                return actualConditionElementFromParentValue !== conditionalValue;
            }
            return actualConditionElementFromParentValue === conditionalValue;
        }
        return false;
    });
}
export function filterDataBySchema(schema, data, options) {
    const normalizedOptions = {
        ...defaultFilterDataBySchemaOptions,
        ...options,
    };
    const filteredData = { ...data };
    schema.forEach((element) => {
        if (element.type === undefined ||
            element.conditional === undefined ||
            filteredData[element.name] === undefined ||
            filteredData[element.name] === null) {
            return;
        }
        const conditionsMatched = areElementConditionalsMatching(element, schema, data, normalizedOptions);
        if (!conditionsMatched) {
            delete filteredData[element.name];
        }
    });
    schema.forEach((element) => {
        if (element.type === undefined || filteredData[element.name] === undefined || filteredData[element.name] === null) {
            return;
        }
        if (element.type === 'group' && Array.isArray(element.children) && filteredData[element.name]) {
            const elementData = filteredData[element.name];
            const childrenSchema = element.children;
            if (Array.isArray(elementData)) {
                filteredData[element.name] = elementData.map((itemData) => filterDataBySchema(childrenSchema, itemData, {
                    peerDependencySchema: normalizedOptions.peerDependencySchema,
                    peerDependencyValue: normalizedOptions.peerDependencyValue,
                    parentSchema: schema,
                    parentValue: data,
                }));
            }
        }
    });
    return filteredData;
}
//# sourceMappingURL=SchemaUtils.js.map